import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useChatContext } from './store/ChatContext';
import { UIMessage } from './types/chat.types';
import { IoSend } from 'react-icons/io5';

interface Props {
  onSend: (text: string) => void;
  isBlocked?: boolean;
  placeholder?: string;
  /**
   * Placeholder shown while `isBlocked` is set — i.e. the reason the composer is
   * unavailable. Host apps pass a localized, situation-specific string (chat
   * disabled, rate-limit countdown, …); the generic fallback is a last resort.
   */
  blockedPlaceholder?: string;
  /**
   * Conversation-starter chips for an empty chat. These used to arrive from the
   * backend as metadata on a persisted English opener message; that opener is
   * gone (it anchored the model to English \u2014 see
   * plans/bot-language-detection.md), so the host app supplies them from its
   * own translations instead, already in the site language.
   */
  starterReplies?: Array<{ label: string; value: string }>;
}

const ChatInput: React.FC<Props> = ({
  onSend,
  isBlocked = false,
  placeholder = 'Type a message\u2026',
  blockedPlaceholder = 'Chat disabled',
  starterReplies,
}) => {
  const { state, dispatch } = useChatContext();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDisabled = state.isStreaming || isBlocked || state.isLoadingHistory;

  // A send that failed before reaching the bot hands its text back here, so the
  // user can retry (or edit) instead of retyping. See REVERT_PENDING_SEND.
  useEffect(() => {
    if (state.pendingInputRestore === null) return;
    setText(state.pendingInputRestore);
    dispatch({ type: 'CONSUME_INPUT_RESTORE' });
  }, [state.pendingInputRestore, dispatch]);

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || isDisabled) return;
    const userMsg: UIMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: trimmed,
      timestamp: new Date().toISOString(),
    };
    // Latch the previous turn's option lists as superseded BEFORE appending
    // the user message (the reducer marks the current last message).
    dispatch({ type: 'MARK_LAST_SUPERSEDED' });
    dispatch({ type: 'ADD_MESSAGE', message: userMsg });
    onSend(trimmed);
  };

  const handleSend = () => {
    submit(text);
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleChipClick = (value: string) => {
    // ADD_MESSAGE clears chips automatically on user-role messages, so we
    // don't need a separate CLEAR_SUGGESTED_REPLIES dispatch here.
    submit(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 96) + 'px';
    }
  };

  // Starters stand in for the turn's chips only while the chat is genuinely
  // empty — once anything has been said, the LLM's own suggested replies (which
  // are written in the conversation language) take over.
  const chips =
    state.suggestedReplies.length > 0
      ? state.suggestedReplies
      : state.messages.length === 0 && !state.isStreaming
      ? starterReplies ?? []
      : [];

  return (
    <>
      {chips.length > 0 && (
        <div className="bot-chips">
          {chips.map((chip, i) => (
            <button
              key={`${chip.label}-${i}`}
              className="bot-chips__btn"
              type="button"
              onClick={() => handleChipClick(chip.value)}
              disabled={isDisabled}
              title={chip.value}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
      <div className={`bot-input ${isDisabled ? 'bot-input--disabled' : ''}`}>
        <textarea
          ref={textareaRef}
          className="bot-input__textarea"
          placeholder={isBlocked ? blockedPlaceholder : placeholder}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          rows={1}
        />
        <button
          className="bot-input__send"
          onClick={handleSend}
          disabled={isDisabled || !text.trim()}
          aria-label="Send"
        >
          <IoSend size={18} />
        </button>
      </div>
    </>
  );
};

export default ChatInput;
